// @ts-check
/**
 * Tenencias (occupancies) — fuente de verdad del histórico de ocupantes de cada
 * unidad y base de la proración de gastos comunes por cambio de ocupante a mitad
 * de mes.
 *
 * Motivación: la relación usuario↔unidad en Habitia era binaria y sin fechas
 * (`apartments.owner/tenant` + `userbuilding`). Esto impedía:
 *   1. Prorratear los gastos comunes cuando un ocupante deja la unidad a mitad
 *      de mes y otro entra (cada uno paga la parte del mes que ocupó).
 *   2. Conservar el acceso del ocupante saliente a su histórico (al sobrescribir
 *      owner/tenant y borrar userbuilding, perdía la visibilidad del edificio).
 *
 * Esta colección es ADITIVA: `apartments.owner/tenant/*Secondary` se mantiene
 * como denormalización del ocupante VIGENTE (toda query existente sigue igual).
 * `occupancies` registra el histórico con dateFrom/dateTo.
 *
 * Fase 0C: el cliente ya no escribe tenencias. Abrirlas y cerrarlas, la baja sin
 * reemplazo, la corrección de fechas y el bloqueo de acceso los hace el servidor
 * (callables inviteResident, importarUnidades, assignOccupants y occupancyAction
 * de functions/identity; ver src/utils/identityApi.js). Acá quedan los helpers
 * puros y las lecturas.
 *
 * Schema de `occupancies/{autoId}`:
 *   occupancyId   number     // contador "occupancyId", lo reserva el servidor
 *   buildingid    number     // denormalizado (las queries filtran por edificio)
 *   apartmentId   string     // doc id de apartments (igual semántica que payments.apartmentId)
 *   apartmentNumber string|null
 *   userid        number     // ocupante
 *   role          "owner" | "tenant"
 *   slot          "primary" | "secondary"  // owner/ownerSecondary, tenant/tenantSecondary
 *   dateFrom      string     // "YYYY-MM-DD" inclusive
 *   dateTo        string|null// "YYYY-MM-DD" inclusive, o null = vigente
 *   active        boolean    // true si dateTo == null (query barata)
 *   accessBlocked boolean    // true = admin bloqueó el acceso a esta unidad para este saliente
 *   accessBlockedAt timestamp|null
 *   accessBlockedBy number|null
 *   createdAt     serverTimestamp
 *   endedAt       timestamp|null
 *   endedReason   string|null// "transfer" | "manual" | "migration"
 *
 * Los helpers de fecha y de proración son PUROS (sin Firestore) y testeables.
 */
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { tolerantId } from "./firestoreHelpers";

// ---------------------------------------------------------------------------
// Helpers de fecha PUROS (sin dependencia de Firestore ni de la zona horaria
// local — todo se calcula en UTC para evitar corrimientos por DST).
// Las fechas son strings ISO "YYYY-MM-DD" y se comparan lexicográficamente,
// lo que es correcto porque están zero-padded.
// ---------------------------------------------------------------------------

/** @param {string} iso @returns {Date} */
const isoToUTCDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};

/** @param {Date} date @returns {string} */
const utcDateToISO = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const maxISO = (a, b) => (a > b ? a : b);
const minISO = (a, b) => (a < b ? a : b);

/**
 * Cantidad de días INCLUSIVE entre dos fechas ISO (ej. "2026-03-01".."2026-03-15" = 15).
 * Devuelve 0 si el rango es inverso.
 * @param {string} fromISO
 * @param {string} toISO
 * @returns {number}
 */
export function diffDaysInclusive(fromISO, toISO) {
  const ms = isoToUTCDate(toISO).getTime() - isoToUTCDate(fromISO).getTime();
  if (ms < 0) return 0;
  return Math.round(ms / 86400000) + 1;
}

/**
 * Día anterior a una fecha ISO. Usado para cerrar la tenencia saliente cuando
 * entra una nueva (entrante el 16 → saliente hasta el 15).
 * @param {string} iso
 * @returns {string}
 */
export function minusOneDay(iso) {
  const d = isoToUTCDate(iso);
  d.setUTCDate(d.getUTCDate() - 1);
  return utcDateToISO(d);
}

/**
 * Día siguiente a una fecha ISO. Usado para el inicio EXCLUSIVO de la ventana de
 * corte (la ventana es (start, end]; el primer día contable es start + 1).
 * @param {string} iso
 * @returns {string}
 */
export function plusOneDay(iso) {
  const d = isoToUTCDate(iso);
  d.setUTCDate(d.getUTCDate() + 1);
  return utcDateToISO(d);
}

/**
 * Límites INCLUSIVOS [start, end] a usar en la proración. Si se pasa una ventana de
 * corte `windowBounds` {start, end} (convención (start, end]), el primer día contable
 * es `start + 1` y el último es `end`. Sin ventana, cae al mes calendario (monthBounds).
 * @param {number} month @param {number} year
 * @param {{start?: string, end?: string}|null} [windowBounds]
 * @returns {{ start: string, end: string, daysInMonth: number }}
 */
function resolveBounds(month, year, windowBounds = null) {
  if (windowBounds && windowBounds.start && windowBounds.end) {
    const start = plusOneDay(String(windowBounds.start).slice(0, 10)); // inicio exclusivo → +1
    const end = String(windowBounds.end).slice(0, 10);
    return { start, end, daysInMonth: diffDaysInclusive(start, end) };
  }
  return monthBounds(month, year);
}

/**
 * Límites de un mes (month es 1-12).
 * @param {number} month @param {number} year
 * @returns {{ start: string, end: string, daysInMonth: number }}
 */
export function monthBounds(month, year) {
  const mm = String(month).padStart(2, "0");
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    start: `${year}-${mm}-01`,
    end: `${year}-${mm}-${String(daysInMonth).padStart(2, "0")}`,
    daysInMonth,
  };
}

/**
 * Días que una tenencia ocupó DENTRO de un mes (ambos extremos inclusive).
 * dateTo null = vigente → cuenta hasta fin de mes.
 * @param {{dateFrom?: string|null, dateTo?: string|null}} occ
 * @param {number} month @param {number} year
 * @returns {number}
 */
export function daysOverlapInMonth(occ, month, year, windowBounds = null) {
  const { start, end } = resolveBounds(month, year, windowBounds);
  const from = occ?.dateFrom ? maxISO(String(occ.dateFrom), start) : start;
  const to = occ?.dateTo ? minISO(String(occ.dateTo), end) : end;
  if (from > to) return 0;
  return diffDaysInclusive(from, to);
}

/**
 * ¿Alguna tenencia de la lista solapa el mes dado? Usado para decidir si un
 * residente histórico puede ver los documentos de un período.
 * @param {Array<{dateFrom?: string|null, dateTo?: string|null}>} occList
 * @param {number} month @param {number} year
 * @returns {boolean}
 */
export function occupancyCoversPeriod(occList, month, year) {
  return (occList || []).some((o) => daysOverlapInMonth(o, month, year) > 0);
}

// ---------------------------------------------------------------------------
// Proración PURA. Dos modos:
//   - distributeByDayWeights: reparte un monto entre ítems que ya tienen `days`
//     (último ítem se lleva el resto → cierra el centavo perdido).
//   - resolveDailyResponsibility: resuelve, día a día, quién es responsable del
//     gasto común (inquilino del día; si no hay, propietario del día; si tampoco,
//     fallback). Maneja transiciones múltiples y tramos vacantes (decisión: el
//     tramo vacante lo paga el propietario).
// ---------------------------------------------------------------------------

const defaultRound = (x) => Math.round((Number(x) + Number.EPSILON) * 100) / 100;

/**
 * Reparte `amount` entre `items` proporcionalmente a `item.days`. El último ítem
 * recibe `amount - sumaPrevia` para que la suma cierre exacta (centavo perdido).
 * @param {number} amount
 * @param {Array<{days:number}&Record<string,any>>} items
 * @param {(x:number)=>number} [roundFn]
 * @returns {Array<{days:number, amount:number}&Record<string,any>>}
 */
export function distributeByDayWeights(amount, items, roundFn = defaultRound) {
  const valid = (items || []).filter((it) => (it?.days || 0) > 0);
  const totalDays = valid.reduce((s, it) => s + it.days, 0);
  if (totalDays <= 0 || valid.length === 0) return [];
  let distributed = 0;
  return valid.map((it, i) => {
    let part;
    if (i === valid.length - 1) {
      part = roundFn(amount - distributed);
    } else {
      part = roundFn((amount * it.days) / totalDays);
      distributed += part;
    }
    return { ...it, amount: part };
  });
}

/**
 * Conveniencia: reparte `amount` entre tramos (occupancies) por sus días dentro
 * del mes. Para montos que SIEMPRE corresponden al mismo rol (ej. fondo de
 * reserva → propietario, o criterio "owner").
 * @param {number} amount
 * @param {Array<{userid:any, role?:string, slot?:string, dateFrom?:string|null, dateTo?:string|null}>} tramos
 * @param {number} month @param {number} year
 * @param {(x:number)=>number} [roundFn]
 * @returns {Array<{userid:any, days:number, amount:number}&Record<string,any>>}
 */
export function splitByDays(amount, tramos, month, year, roundFn = defaultRound, windowBounds = null) {
  const withDays = (tramos || []).map((t) => ({
    ...t,
    days: daysOverlapInMonth(t, month, year, windowBounds),
  }));
  return distributeByDayWeights(amount, withDays, roundFn);
}

/**
 * Resuelve, día a día del mes, quién es el responsable del gasto común de la
 * unidad y devuelve los días acumulados por (rol, userid).
 *
 * Regla por día:
 *   1. Si hay un INQUILINO ocupando ese día → paga el inquilino.
 *   2. Si no, si hay un PROPIETARIO ese día → paga el propietario (tramo vacante
 *      de inquilino: lo cubre el propietario).
 *   3. Si no hay ninguno → fallbackOwnerId (ej. `unit_<id>` sintético).
 *
 * @param {Object} params
 * @param {Array<{userid:any, dateFrom?:string|null, dateTo?:string|null}>} [params.tenantTramos]
 * @param {Array<{userid:any, dateFrom?:string|null, dateTo?:string|null}>} [params.ownerTramos]
 * @param {number} params.month @param {number} params.year
 * @param {any} [params.fallbackOwnerId]
 * @returns {Array<{userid:string, role:"owner"|"tenant", days:number}>}
 */
export function resolveDailyResponsibility({
  tenantTramos = [],
  ownerTramos = [],
  month,
  year,
  fallbackOwnerId = null,
  windowBounds = null,
}) {
  const { daysInMonth, start, end } = resolveBounds(month, year, windowBounds);
  const covers = (t, dayISO) => {
    if (t?.userid == null || String(t.userid).trim() === "") return false;
    const from = t.dateFrom ? String(t.dateFrom) : start;
    const to = t.dateTo ? String(t.dateTo) : "9999-12-31";
    return from <= dayISO && dayISO <= to;
  };
  /** @type {Map<string, {userid:string, role:"owner"|"tenant", days:number}>} */
  const counts = new Map();
  // Itera día a día de start..end inclusive (la ventana puede cruzar el fin de mes).
  for (let i = 0; i < daysInMonth; i++) {
    const cursor = isoToUTCDate(start);
    cursor.setUTCDate(cursor.getUTCDate() + i);
    const dayISO = utcDateToISO(cursor);
    if (dayISO > end) break;
    let resp = null;
    const t = tenantTramos.find((x) => covers(x, dayISO));
    if (t) resp = { userid: String(t.userid), role: /** @type {const} */ ("tenant") };
    else {
      const o = ownerTramos.find((x) => covers(x, dayISO));
      if (o) resp = { userid: String(o.userid), role: /** @type {const} */ ("owner") };
      else if (fallbackOwnerId != null)
        resp = { userid: String(fallbackOwnerId), role: /** @type {const} */ ("owner") };
    }
    if (!resp) continue;
    const key = `${resp.role}|${resp.userid}`;
    const prev = counts.get(key) || { userid: resp.userid, role: resp.role, days: 0 };
    prev.days += 1;
    counts.set(key, prev);
  }
  return Array.from(counts.values());
}

// ---------------------------------------------------------------------------
// Puente rol/slot ↔ campo de apartments. `userbuilding.condition` usa
// "Propietario"/"Inquilino"; acá normalizamos a role/slot.
// ---------------------------------------------------------------------------

/** @param {string} field @returns {{role:"owner"|"tenant", slot:"primary"|"secondary"}|null} */
export function roleAndSlotForField(field) {
  switch (field) {
    case "owner":
      return { role: "owner", slot: "primary" };
    case "ownerSecondary":
      return { role: "owner", slot: "secondary" };
    case "tenant":
      return { role: "tenant", slot: "primary" };
    case "tenantSecondary":
      return { role: "tenant", slot: "secondary" };
    default:
      return null;
  }
}

/** @param {"owner"|"tenant"} role @param {"primary"|"secondary"} [slot] @returns {string|null} */
export function fieldForRoleSlot(role, slot = "primary") {
  const secondary = slot === "secondary";
  if (role === "owner") return secondary ? "ownerSecondary" : "owner";
  if (role === "tenant") return secondary ? "tenantSecondary" : "tenant";
  return null;
}

/** @param {string} condition @returns {"owner"|"tenant"|null} */
export const roleForCondition = (condition) =>
  condition === "Propietario" ? "owner" : condition === "Inquilino" ? "tenant" : null;

/** @param {"owner"|"tenant"} role @returns {"Propietario"|"Inquilino"|null} */
export const conditionForRole = (role) =>
  role === "owner" ? "Propietario" : role === "tenant" ? "Inquilino" : null;

/**
 * dateFrom sentinel para tenencias legacy sin fecha de inicio conocida (datos
 * previos a esta colección). Es anterior a cualquier cierre real, así que cubre
 * desde el inicio de cualquier mes. El servidor usa el mismo valor
 * (functions/identity/core.js).
 */
export const SENTINEL_START = "2000-01-01";

// ---------------------------------------------------------------------------
// Accesores Firestore. Solo filtros de igualdad ('==' / 'in'), que Firestore
// resuelve combinando índices simples (sin índice compuesto): el filtrado fino
// se hace en cliente, igual que el resto del código.
// ---------------------------------------------------------------------------

/**
 * Tenencias de un edificio que solapan un mes (para la proración del cierre).
 * @param {string|number} buildingid @param {number} month @param {number} year
 * @returns {Promise<Array<Object>>}
 */
export async function getOccupanciesOverlappingMonth(buildingid, month, year, windowBounds = null) {
  const snap = await getDocs(
    query(collection(db, "occupancies"), where("buildingid", "in", tolerantId(buildingid)))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((o) => daysOverlapInMonth(o, month, year, windowBounds) > 0);
}

/**
 * Todas las tenencias de un edificio (activas + cerradas) — para listados de
 * administración (ej. marcar ocupantes salientes).
 * @param {string|number} buildingid
 * @returns {Promise<Array<Object>>}
 */
export async function getOccupanciesForBuilding(buildingid) {
  if (buildingid == null || buildingid === "") return [];
  const snap = await getDocs(
    query(collection(db, "occupancies"), where("buildingid", "in", tolerantId(buildingid)))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Todas las tenencias de un usuario (activas + cerradas) — para retención de
 * acceso histórico.
 *
 * Sin `buildingid`, solo sirve para las tenencias PROPIAS (las reglas 0D las
 * dejan listar por user_access.userids). Cuando las lee otra persona (el staff
 * en "Ver como residente") hay que pasar el edificio: las reglas solo le dejan
 * listar tenencias de un edificio donde es staff (C6).
 * @param {string|number} userid
 * @param {{ buildingid?: string|number|null }} [scope]
 * @returns {Promise<Array<Object>>}
 */
export async function getOccupanciesForUser(userid, { buildingid = null } = {}) {
  if (userid == null || userid === "") return [];
  const filtros = [where("userid", "in", tolerantId(userid))];
  if (buildingid != null && buildingid !== "") filtros.push(where("buildingid", "in", tolerantId(buildingid)));
  const snap = await getDocs(query(collection(db, "occupancies"), ...filtros));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Historial de ocupantes de una unidad, ordenado por dateFrom descendente.
 * El edificio es obligatorio: las reglas 0D solo dejan al staff listar las
 * tenencias de su edificio (C6). El id del doc va después del spread para que
 * un campo `id` guardado no lo pise.
 * @param {string} apartmentId
 * @param {string|number} buildingid
 * @returns {Promise<Array<Object>>}
 */
export async function getOccupanciesForApartment(apartmentId, buildingid) {
  if (!apartmentId || buildingid == null || buildingid === "") return [];
  const snap = await getDocs(
    query(
      collection(db, "occupancies"),
      where("apartmentId", "==", String(apartmentId)),
      where("buildingid", "in", tolerantId(buildingid))
    )
  );
  return snap.docs
    .map((d) => ({ ...d.data(), id: d.id }))
    .sort((a, b) => String(b.dateFrom || "").localeCompare(String(a.dateFrom || "")));
}
